import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { InstitutionFieldsetPerson } from '@/features/institution/components/fieldsets/person';
import type { InstitutionOwnershipOrDefaults } from '@/features/institution/ownership/schema';
import { createInstitutionPerson } from '@/features/institution/schemas/person';

const InstitutionOwnershipFieldsetUbosLegend = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { t } = useTranslation();
  const ubosKey = form.watch('hasOwners') === 'NO' ? 'seniorManagingOfficial' : 'ubos';

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t(`institutionDetails.ownership.${ubosKey}.title`)}</PageHeadingTitle>
        <PageHeadingDescription>{t(`institutionDetails.ownership.${ubosKey}.description`)}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionOwnershipFieldsetUbosLegend.displayName = 'InstitutionOwnershipFieldsetUbosLegend';

const InstitutionOwnershipFieldsetUbo = ({ index, onRemove }: { index: number; onRemove?: () => void }) => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { t } = useTranslation();
  const hasOwners = form.watch('hasOwners') === 'YES';

  return (
    <Card className="shadow-none" bordered>
      <CardContent className="pt-6">
        <fieldset className="relative space-y-6">
          <legend className="font-medium">
            {hasOwners ? `${t('institutionDetails.ownership.ubos.owner')} ${index + 1}` : t('institutionDetails.ownership.seniorManagingOfficial.seniorManagingOfficial')}
          </legend>
          {onRemove ? (
            <Button className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive" size="icon" type="button" variant="ghost" onClick={onRemove}>
              <Trash2 className="size-4" />
              <span className="sr-only">
                {t('institutionDetails.ownership.ubos.remove')} {index + 1}
              </span>
            </Button>
          ) : null}
          <InstitutionFieldsetPerson<InstitutionOwnershipOrDefaults> name={`ubos.${index}`} />
          {hasOwners ? (
            <FormField
              control={form.control}
              name={`ubos.${index}.ownershipPct`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.ownership.ubos.ownershipPct')}*</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="no-spinner"
                      inputMode="decimal"
                      placeholder={t('institutionDetails.ownership.placeholders.percentage')}
                      type="number"
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value === '' ? undefined : Number(event.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          <FormField
            control={form.control}
            name={`ubos.${index}.isPep`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.ownership.ubos.isPep')}*</FormLabel>
                <FormDescription>{t('institutionDetails.person.isPepDescription')}</FormDescription>
                <FormControl>
                  <RadioGroup
                    className="grid-cols-2"
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.ownership.ubos.isPep')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
      </CardContent>
    </Card>
  );
};
InstitutionOwnershipFieldsetUbo.displayName = 'InstitutionOwnershipFieldsetUbo';

const InstitutionOwnershipFieldsetUbos = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'ubos' });
  const { t } = useTranslation();
  const hasOwners = form.watch('hasOwners') === 'YES';

  return (
    <FormField
      control={form.control}
      name="ubos"
      render={({ fieldState: { error } }) => (
        <FormItem>
          <div className="space-y-6">
            {fields.map((ubo, index) => (
              <InstitutionOwnershipFieldsetUbo key={ubo.id} index={index} onRemove={fields.length > 1 ? () => remove(index) : undefined} />
            ))}
            {hasOwners ? (
              <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append(createInstitutionPerson())}>
                <Plus className="size-4" />
                {t('institutionDetails.ownership.ubos.add')}
              </Button>
            ) : null}
          </div>
          {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
        </FormItem>
      )}
    />
  );
};
InstitutionOwnershipFieldsetUbos.displayName = 'InstitutionOwnershipFieldsetUbos';

export { InstitutionOwnershipFieldsetUbos, InstitutionOwnershipFieldsetUbosLegend };
